import { useQuery } from "@tanstack/react-query";
import { useAxios } from "auth-sdk";
import { addAction, addError } from "@wayflyer/blackbox-fe";
import { FlyError, Link, Text, useToast } from "@wayflyer/flyui";
import { analytics, useErrorModal } from "shared-ui";
import { queryClient, handleContactTeam, useAnalyticsContext } from "shared-utils";
import type { ReactNode } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { usePlaidLink } from "react-plaid-link";

import type { BankConnectionSchema } from "../api-types/bankconnect_schemas";

const PLAID_LINK_TOKEN_KEY = "plaid-link-token";
const PLAID_CONNECTION_TO_BE_UPDATED_KEY = "plaid-account-update";

function getStoredToken() {
  return localStorage.getItem(PLAID_LINK_TOKEN_KEY);
}

function setStoredToken(token: string) {
  localStorage.setItem(PLAID_LINK_TOKEN_KEY, token);
}

function clearStoredToken() {
  localStorage.removeItem(PLAID_LINK_TOKEN_KEY);
}

function getStoredConnectionIdToBeUpdated(): string {
  return localStorage.getItem(PLAID_CONNECTION_TO_BE_UPDATED_KEY);
}

function setStoredConnectionIdToBeUpdated(accountId: string) {
  localStorage.setItem(PLAID_CONNECTION_TO_BE_UPDATED_KEY, accountId);
}

function clearStoredConnectionIdToBeUpdated() {
  localStorage.removeItem(PLAID_CONNECTION_TO_BE_UPDATED_KEY);
}

function clearBankConnectStorage() {
  clearStoredConnectionIdToBeUpdated();
  clearStoredToken();
}

function isPlaidOauthCallback() {
  const url = new URL(window.location.href);
  return url.searchParams.has("oauth_state_id");
}

type PlaidBankConnectProps = {
  plaidLinkRedirectUri: string;
  bankConnections: BankConnectionSchema[];
  connectAccountPath: string;
  accountReconnectedPath: string;
  getLinkTokenPath: string;
  getUpdateTokenPath: string;
  onPlaidEvent?: (event: string, metadata: any) => void;
  onPlaidExit?: (metadata?: any) => void;
  onAccountConnected?: () => void;
  onAccountReconnected?: () => void;
  onPlaidOpen?: () => void;
  messageSuccess?: (message: string) => void;
  messageError?: (message: ReactNode) => void;
};

type PlaidLinkTokenResponse = {
  token: string;
};

/**
 * A react hook that wraps the underlying usePlaidLink hook and adds
 * logic for creating the bank connections in our backend
 * and for managing the bank connections state.
 *
 * It also encapsulates the logic for handling the plaid Oauth flow,
 * reading and writing of link tokens to session storage.
 */
function useBankConnectWithPlaid({
  plaidLinkRedirectUri,
  bankConnections: initialBankConnections,
  connectAccountPath,
  accountReconnectedPath,
  getLinkTokenPath,
  getUpdateTokenPath,
  onPlaidEvent,
  onAccountConnected,
  onAccountReconnected,
  onPlaidExit,
  onPlaidOpen,
  messageSuccess,
  messageError,
}: PlaidBankConnectProps) {
  const axios = useAxios();
  const intl = useIntl();
  const toaster = useToast();
  const { openErrorModal } = useErrorModal();

  const successToast = (msg: string) => toaster.positive(msg);
  const errorModal = useCallback(
    (msg: ReactNode) =>
      openErrorModal(
        new FlyError({ title: "Error", message: msg, error: new Error("Bank connection error"), suppressLog: true })
      ),
    [openErrorModal]
  );
  const success = messageSuccess ?? successToast;
  const error = messageError ?? errorModal;
  const analyticsContext = useAnalyticsContext();
  const [bankConnections, setBankConnections] = useState<BankConnectionSchema[]>(initialBankConnections);

  const [isOauthCallback, setIsOauthCallback] = useState(isPlaidOauthCallback());
  const [plaidOpenedOnce, setPlaidOpenedOnce] = useState(false);

  const [updateToken, setUpdateToken] = useState(null);
  const [plaidOpenState, setPlaidOpenState] = useState<"closed" | "open" | "opening">("closed");

  // Mutable object which can be "read" by callbacks to prevent callback event firing from double plaid events
  const latestPlaidEvent = useRef(null);

  const fetchPlaidLinkToken = () =>
    axios.post<PlaidLinkTokenResponse>(getLinkTokenPath, {
      redirect_uri: plaidLinkRedirectUri,
    });

  const {
    isLoading: isLinkTokenLoading,
    isFetching: isLinkTokenFetching,
    data: linkToken,
  } = useQuery({
    queryKey: [getLinkTokenPath],
    queryFn: fetchPlaidLinkToken,
    select: ({ data }) => data.token,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const bankReconnected = useCallback(
    async (bankConnection: BankConnectionSchema) => {
      try {
        const result = await axios.post(accountReconnectedPath, { account_id: bankConnection.id });
        // eslint-disable-next-line no-console
        console.log("bank reconnected result", result);
      } catch (e) {
        addError(e);
      }
    },
    [accountReconnectedPath, axios]
  );

  const connectBank = useCallback(
    async (token, metadata) => {
      try {
        await axios.post(connectAccountPath, {
          token,
          institution: {
            name: metadata.institution.name,
            id: metadata.institution.institution_id,
          },
          accounts: metadata.accounts.map(({ id, mask, name, type, subtype }) => ({
            id,
            mask,
            name,
            type,
            subtype,
          })),
        });
        onAccountConnected && onAccountConnected();
        analytics.bankStatementsAccountConnected(analyticsContext);
        success(
          intl.formatMessage(
            {
              id: "bankconnect: account connected success message",
              defaultMessage: "{institutionName} successfully connected",
            },
            {
              institutionName: metadata.institution.name,
            }
          )
        );
      } catch (e) {
        addError(e);
        error(
          <FormattedMessage
            id="bankconnect: unable to connect account error message"
            defaultMessage="<p>Unable to connect bank account. If the problem persists please <a>contact our support team</a>.</p>"
            values={{
              p: (chunks) => <Text>{chunks}</Text>,
              a: (chunks) => <Link onClick={handleContactTeam}>{chunks}</Link>,
            }}
          />
        );
        analytics.bankStatementsConnectAccountFailed(analyticsContext);
        setBankConnections((nextBankConnections) =>
          nextBankConnections.filter((connection) => connection.id !== metadata.account_id)
        );
      }
    },
    [axios, connectAccountPath, onAccountConnected, analyticsContext, success, intl, error]
  );

  const onNewBankConnection = useCallback(
    (token, metadata) => {
      setBankConnections((currentBankConnections) => {
        connectBank(token, metadata);
        const id = metadata.account_id;
        const institutionToBeAdded = metadata.institution;

        return [
          ...currentBankConnections,
          {
            providerType: "plaid",
            accountsSelected: true,
            institutionID: institutionToBeAdded.institution_id,
            institutionName: institutionToBeAdded.institution_name,
            id,
            accessible: true,
            accounts: [], // TODO: do we need to do anything here?
          },
        ];
      });
    },
    [connectBank]
  );

  const onReconnection = useCallback(() => {
    const updatedConnectionId = getStoredConnectionIdToBeUpdated();
    const updatedConnection = bankConnections.find((conn) => conn.id === updatedConnectionId);

    bankReconnected(updatedConnection);
    setBankConnections((currentBankConnections) => {
      return currentBankConnections.map((existingConnection) => {
        if (existingConnection.id === updatedConnection.id) {
          return {
            ...existingConnection,
            accessible: true,
          };
        }
        return existingConnection;
      });
    });
    analytics.bankStatementsAccountReconnected(analyticsContext);
    success(
      intl.formatMessage(
        {
          id: "bankconnect: account reconnected success message",
          defaultMessage: "{institutionName} successfully reconnected",
        },
        { institutionName: updatedConnection.institutionName }
      )
    );
    if (updateToken) {
      setUpdateToken(null);
    }
    clearStoredConnectionIdToBeUpdated();
    onAccountReconnected && onAccountReconnected();
  }, [bankConnections, bankReconnected, analyticsContext, success, intl, updateToken, onAccountReconnected]);

  const clearOauthCallbackState = useCallback(() => {
    if (isOauthCallback) {
      window.history.replaceState({}, document.title, `${window.location.origin}${window.location.pathname}`);
      setIsOauthCallback(false);
    }
  }, [isOauthCallback]);

  const onPlaidSuccessCallback = useCallback(
    (token, metadata) => {
      if (getStoredConnectionIdToBeUpdated()) {
        onReconnection();
      } else {
        onNewBankConnection(token, metadata);
      }
      clearOauthCallbackState();
    },
    [clearOauthCallbackState, onNewBankConnection, onReconnection]
  );

  const onPlaidHandoffOrExitEvent = useCallback(
    async (event: string, metadata) => {
      // Somtimes the plaid modal will immediately close during the opening state
      setPlaidOpenState("closed");
      if (updateToken) {
        setUpdateToken(null);
      }

      await queryClient.invalidateQueries({
        queryKey: [getLinkTokenPath],
      });

      // Only clean up these on EXIT because the HANDOFF event
      // can fire before the plaid onSuccess callback
      // where we need some of the state in the connect/reconnect handlers
      // those handlers clean these up instead
      if (event === "EXIT") {
        clearStoredConnectionIdToBeUpdated();
        clearOauthCallbackState();
        onPlaidExit && onPlaidExit(metadata);
      }
    },
    [updateToken, clearOauthCallbackState, onPlaidExit, getLinkTokenPath]
  );

  const handlePlaidEventAnalytics = useCallback(
    (event, metadata) => {
      if (event === "OPEN") {
        if (updateToken) {
          analytics.bankStatementsReconnectBegin(analyticsContext);
        } else {
          analytics.bankStatementsConnectBegin(analyticsContext);
        }
      } else if (event === "EXIT") {
        analytics.bankStatementsConnectClosed(analyticsContext);
      } else if (event === "ERROR") {
        analytics.bankStatementsPlaidError(analyticsContext, metadata);
      }
    },
    [analyticsContext, updateToken]
  );

  const emitPlaidLinkDataDogAction = useCallback(
    (event, metadata) => {
      let actionName;
      if (event === "OPEN") {
        actionName = "Open";
      } else if (event === "SELECT_INSTITUTION") {
        actionName = "Select Institution";
      } else if (event === "HANDOFF") {
        actionName = "Success";
      } else if (event === "EXIT") {
        actionName = "Abandoned";
      } else if (event === "ERROR") {
        actionName = "Error";
      } else {
        return;
      }

      const attributes = {
        error_code: metadata.error_code,
        exit_status: metadata.exit_status,
        institution_id: metadata.institution_id,
        institution_name: metadata.institution_name,
        has_connections: bankConnections && Array.isArray(bankConnections) && bankConnections.length > 0,
        reconnection: !!updateToken,
      };
      addAction(`Plaid Link ${actionName}`, attributes);
    },
    [updateToken, bankConnections]
  );

  const onPlaidEventCallback = useCallback(
    (event: string, metadata: any) => {
      handlePlaidEventAnalytics(event, metadata);
      emitPlaidLinkDataDogAction(event, metadata);

      // also check for TRANSITION_VIEW here because
      // when the window opens after an oauth based reconnect,
      // the open event doesn't fire but the TRANSITION_VIEW one does
      // TRANSITION_VIEW fires when change "screens" within the plaid modal
      // so it's safe to say if this even happens, the modal is open
      if ((event === "OPEN" || event === "TRANSITION_VIEW") && latestPlaidEvent.current !== event) {
        latestPlaidEvent.current = event;
        if (!plaidOpenedOnce) {
          setPlaidOpenedOnce(true);
        }
        setPlaidOpenState("open");
      }
      // either HANDOFF or EXIT (but not both) will fire when the plaid modal closes
      // both of these events are cues to clean up some state
      // HANDOFF fires when the user exits plaid link with a successful connect/reconnect
      // EXIT fires when the user exits plaid link, either due to an error or because they've abandoned the flow
      else if ((event === "HANDOFF" || event === "EXIT") && latestPlaidEvent.current !== event) {
        latestPlaidEvent.current = event;
        onPlaidHandoffOrExitEvent(event, metadata);
      }
      onPlaidEvent && onPlaidEvent(event, metadata);
    },
    [handlePlaidEventAnalytics, emitPlaidLinkDataDogAction, onPlaidEvent, plaidOpenedOnce, onPlaidHandoffOrExitEvent]
  );

  // determine which plaid link token should be used
  // If isOauthCallback is false then we're potentially
  // beginning either a reconnect (updateToken) or connect (linkToken) flow.
  // store the token so we'll have it after the Oauth redirect

  // If isOauthCallback is true then it means we're returning
  // to the app after visiting the bank website.
  // In that case get the stored token

  let token = updateToken || linkToken;
  if (isOauthCallback) {
    token = getStoredToken();
    // if we have no stored token on redirect. Something has gone really wrong.
    // display an error and clear the oauth state.
    if (!token) {
      error(
        <FormattedMessage
          id="bankconnect: unable to connect account error message"
          defaultMessage="<p>Unable to connect bank account. If the problem persists please <a>contact our support team</a>.</p>"
          values={{
            p: (chunks) => <Text>{chunks}</Text>,
            a: (chunks) => <Link onClick={handleContactTeam}>{chunks}</Link>,
          }}
        />
      );
      clearOauthCallbackState();
      addError(new Error("bank connect - stored token not found after oauth redirect"));
    }
  } else {
    setStoredToken(token);
  }

  const { ready: plaidReady, open: openPlaidUI } = usePlaidLink({
    token,
    receivedRedirectUri: isOauthCallback ? window.location.href : null,
    onSuccess: onPlaidSuccessCallback,
    onEvent: onPlaidEventCallback,
  });

  const openPlaid = useCallback(() => {
    openPlaidUI();
    // calling plaid's open function doesn't immediately open it
    // there can be some time before it actually opens
    // this "opening" state can be useful to UI elements that use the bank connect feature
    setPlaidOpenState("opening");
    onPlaidOpen && onPlaidOpen();
  }, [openPlaidUI, onPlaidOpen]);

  const initiateLinkMode = useCallback(() => {
    if (!isLinkTokenLoading && !isLinkTokenFetching) {
      clearStoredConnectionIdToBeUpdated();
      openPlaid();
    } else {
      error(
        <FormattedMessage
          id="bankconnect: unable to begin connect"
          defaultMessage="<p>Unable to connect bank account. If the problem persists please <a>contact our support team</a>.</p>"
          values={{
            p: (chunks) => <Text>{chunks}</Text>,
            a: (chunks) => <Link onClick={handleContactTeam}>{chunks}</Link>,
          }}
        />
      );
      addError(new Error("bank connect - plaid link token not ready"));
    }
  }, [error, openPlaid, isLinkTokenLoading, isLinkTokenFetching]);

  const initiateUpdateMode = useCallback(
    async (bankConnection: BankConnectionSchema) => {
      try {
        const {
          data: { token },
        } = await axios.post(getUpdateTokenPath, {
          account_id: bankConnection.id,
          redirect_uri: plaidLinkRedirectUri,
        });
        setUpdateToken(token);
        setStoredConnectionIdToBeUpdated(bankConnection.id);
      } catch {
        error(
          <FormattedMessage
            id="bankconnect: unable to begin reconnect"
            defaultMessage="<p>Unable to reconnect bank account. If the problem persists please <a>contact our support team</a>.</p>"
            values={{
              p: (chunks) => <Text>{chunks}</Text>,
              a: (chunks) => <Link onClick={handleContactTeam}>{chunks}</Link>,
            }}
          />
        );
      }
    },
    [axios, getUpdateTokenPath, plaidLinkRedirectUri, error]
  );

  // usePlaidLink returns ready = true and open = null at one point
  // so you need to check both are truthy values to know it's actually ready
  const isPlaidActuallyReady = plaidReady && openPlaidUI;

  // determine whether plaid link should be opened without "openPlaid" having been called externally
  // if updateToken is present, then update mode has been initiated and plaid should be opened
  // OR
  // if plaid hasn't been opened yet and we are in an Oauth callback
  const shouldPlaidOpenImmediately = updateToken || (!plaidOpenedOnce && isOauthCallback);

  useEffect(() => {
    if (isPlaidActuallyReady && shouldPlaidOpenImmediately) {
      openPlaid();
    }
  }, [
    plaidReady,
    openPlaid,
    plaidOpenedOnce,
    isOauthCallback,
    openPlaidUI,
    updateToken,
    isPlaidActuallyReady,
    shouldPlaidOpenImmediately,
  ]);

  return {
    bankConnections,
    initiateLinkMode,
    isOauthCallback,
    initiateUpdateMode,
    plaidOpenState,
  };
}

export { clearBankConnectStorage, useBankConnectWithPlaid };
